import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PhygitalIcon from "@/components/PhygitalIcon";
import EarlyAccessModal from "@/components/EarlyAccessModal";
import SharedFooter from "@/components/SharedFooter";
import AllCategoriesSection from "@/components/AllCategoriesSection";
import {
  Sparkles,
  ArrowRight,
  Leaf,
  Clock,
  Heart,
  Star,
  Users,
  Calendar,
  CheckCircle2,
  ShoppingBag,
  FileText,
  Shield,
  Zap
} from "lucide-react";

// Cannabis leaf icon (custom since lucide doesn't have one)
const CannabisIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L12 22" />
    <path d="M12 8C12 8 8 6 5 8C8 10 10 12 12 12" />
    <path d="M12 8C12 8 16 6 19 8C16 10 14 12 12 12" />
    <path d="M12 12C12 12 7 11 4 14C8 15 10 17 12 17" />
    <path d="M12 12C12 12 17 11 20 14C16 15 14 17 12 17" />
  </svg>
);

const DispensariesSalesPage = () => {
  const [showEarlyAccessModal, setShowEarlyAccessModal] = useState(false);

  useEffect(() => {
    document.title = "phygital - For Dispensaries";
  }, []);

  const dispensaryFeatures = [
    {
      icon: Heart,
      title: "Customer preference profiles",
      description: "Track preferred strains, consumption methods, and effects for personalized recommendations"
    },
    {
      icon: ShoppingBag,
      title: "Product matching",
      description: "Automatically suggest products based on individual preferences and history"
    },
    {
      icon: FileText,
      title: "Compliance ready",
      description: "Built-in documentation and tracking for regulatory requirements"
    },
    {
      icon: Shield,
      title: "Secure & private",
      description: "Customer data protected with enterprise-grade security"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" variant="light" />
            <span className="font-semibold text-xl">phygital</span>
          </Link>
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <Leaf className="w-3 h-3 mr-1" />
              Dispensaries
              <Badge variant="outline" className="ml-2 text-xs bg-amber-50 text-amber-600 border-amber-200">
                Coming Soon
              </Badge>
            </Badge>
            <Button
              onClick={() => setShowEarlyAccessModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="join-pilot-btn"
            >
              Get Early Access
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-64 h-64 bg-green-200 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-emerald-200 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-4xl mx-auto text-center">
          <Badge className="mb-6 bg-amber-100 text-amber-700 border border-amber-200">
            <Clock className="w-3 h-3 mr-1" />
            Coming Soon - Join the waitlist
          </Badge>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
            <span className="bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 bg-clip-text text-transparent">
              cannabis
            </span>{" "}
            preferences,
            <br />
            perfectly communicated.
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Know your customers' preferred strains, effects, and consumption methods. Deliver personalized recommendations every time.
          </p>
          
          <Button
            onClick={() => setShowEarlyAccessModal(true)}
            size="lg"
            className="bg-green-600 hover:bg-green-700 text-white h-12 px-8 rounded-full"
            data-testid="hero-join-btn"
          >
            Get Early Access
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Features Preview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">
            What we're building
          </h2>
          <p className="text-gray-600 text-center mb-10 max-w-xl mx-auto">
            Help us shape the future of dispensary customer experience. Integrate with your POS or go all-in with phygital.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {dispensaryFeatures.map((feature, idx) => (
              <Card key={idx} className="border-green-100 hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center mb-4">
                    <feature.icon className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Be first in line
          </h2>
          <p className="text-green-100 text-lg mb-8">
            Get early access when we launch dispensary services.
          </p>
          <Button
            onClick={() => setShowEarlyAccessModal(true)}
            size="lg"
            className="bg-white text-green-700 hover:bg-green-50 h-12 px-8 rounded-full font-semibold"
            data-testid="cta-join-btn"
          >
            Get Early Access
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Built for Every Service Industry - Hidden for now */}
      {/* <AllCategoriesSection currentCategory="dispensaries" /> */}

      {/* Footer */}
      <SharedFooter />

      {/* Early Access Modal */}
      <EarlyAccessModal
        open={showEarlyAccessModal}
        onOpenChange={setShowEarlyAccessModal}
        category="dispensary"
      />
    </div>
  );
};

export default DispensariesSalesPage;
